// Types
import { useMemo } from 'react';
import { DropdownMenuCard, EditableList, type IOption } from 'components/Card';
import { OrganizationTreePopover } from 'components/OrganizationTreePopover';
import { parsedCustomerSignatureSelector } from 'slices/auth.slice';
// Slices
import { fieldsSelector } from 'slices/field.slice';
import { selectObjectTypesList } from 'slices/objectTypes.slice';
import { reservationTemplatesSelector } from 'slices/reservationTemplate.slice';
import { useAppSelector } from 'slices/store';
import type { TField, TType } from '@timeedit/types';
import { EFieldType } from '@timeedit/types/lib/enums/field.enum';
// Components
import { TEDrawerSection } from '@timeedit/ui-components';

interface AttributesProps {
  value: TField;
  onChangeValue: (key: string, value: any) => void;
}

const Attributes = ({ value, onChangeValue }: AttributesProps) => {
  const fields = useAppSelector(fieldsSelector);
  const reservationTemplates =
    useAppSelector(reservationTemplatesSelector) || [];
  const objectTypesList: TType[] = useAppSelector(selectObjectTypesList) || [];
  const customerSignature = useAppSelector(parsedCustomerSignatureSelector);

  const referenceFieldsOptions = useMemo(() => {
    return convertDataFieldsOptions({
      field: value,
      fields: fields,
      customerSignature,
    });
  }, [value.fieldType, fields, customerSignature]);

  const reservationTemplatesOptions = useMemo(
    () =>
      reservationTemplates.map<IOption>((item) => ({
        key: String(item.id),
        label: '' + item.name,
        url: `/${customerSignature}/configuration/reservation-templates?item1=${item.id}`,
      })),
    [reservationTemplates, customerSignature]
  );

  const objectTypesOptions = useMemo(
    () =>
      objectTypesList.map<IOption>((objectType) => ({
        key: String(objectType.id),
        label: objectType.name,
        url: `/${customerSignature}/configuration/object-types?item1=${objectType.id}`,
      })),
    [objectTypesList, customerSignature]
  );

  const categoriesEnable = useMemo(() => {
    return value.fieldType === EFieldType.CATEGORY && value.editable === false;
  }, [value.fieldType, value.editable]);

  return (
    <TEDrawerSection label="Attributes">
      <div className="wrapper-section">
        <div className="section-item">
          <div className="te-drawer-input">
            <EditableList
              title="Categories"
              items={value.categories || []}
              readonly={!categoriesEnable}
              onChange={(values) => onChangeValue('categories', values)}
              data-testid="category-add"
            />
          </div>
          <div className="te-drawer-input">
            <DropdownMenuCard
              title="Reservation Templates"
              selectedKeys={(value.reservationTemplates || []).map(String)}
              options={reservationTemplatesOptions as any}
              onChange={(values) =>
                onChangeValue('reservationTemplates', values.map(Number))
              }
              data-testid="reservation-template"
            />
          </div>
        </div>
        <div className="section-item">
          <div className="te-drawer-input">
            <DropdownMenuCard
              title="Reference Fields"
              selectedKeys={value.referenceFields || []}
              options={referenceFieldsOptions}
              onChange={(values) =>
                onChangeValue('referenceFields', values.map(Number))
              }
              data-testid="reference-fields"
            />
          </div>
          <div className="te-drawer-input">
            <OrganizationTreePopover
              title="Organizations"
              value={value.organizations || []}
              onlyLeafNodes
              onChange={(values) => onChangeValue('organizations', values)}
            />
          </div>
        </div>

        <div className="section-item">
          <div className="te-drawer-input">
            <DropdownMenuCard
              title="Object Types"
              selectedKeys={(value.types || []).map(String)}
              options={objectTypesOptions}
              onChange={(values) => onChangeValue('types', values.map(Number))}
              data-testid="object-type"
            />
          </div>
        </div>
      </div>
    </TEDrawerSection>
  );
};

export default Attributes;

const convertDataFieldsOptions = ({
  field,
  fields,
  customerSignature,
}: {
  field: TField;
  fields: TField[];
  customerSignature: string;
}): IOption[] => {
  let dataSourceFields: TField[] = [];
  if (
    field.fieldType === EFieldType.TEXT ||
    field.fieldType === EFieldType.SIGNATURE ||
    field.fieldType === EFieldType.TELEPHONE
  ) {
    dataSourceFields = fields.filter(
      (f: TField) => f.fieldType === EFieldType.REFERENCE
    );
  } else if (field.fieldType === EFieldType.REFERENCE) {
    dataSourceFields = fields.filter((f: TField) =>
      [EFieldType.TEXT, EFieldType.SIGNATURE, EFieldType.TELEPHONE].includes(
        f.fieldType as EFieldType
      )
    );
  } else if (
    field.fieldType === EFieldType.EMAIL ||
    field.fieldType === EFieldType.URL ||
    field.fieldType === EFieldType.NON_SEARCHABLE_TEXT
  ) {
    dataSourceFields = fields.filter(
      (f: TField) => f.fieldType === EFieldType.NON_SEARCHABLE_REFERENCE
    );
  } else if (field.fieldType === EFieldType.NON_SEARCHABLE_REFERENCE) {
    dataSourceFields = fields.filter((f: TField) =>
      [
        EFieldType.URL,
        EFieldType.EMAIL,
        EFieldType.NON_SEARCHABLE_TEXT,
      ].includes(f.fieldType as EFieldType)
    );
  }
  return dataSourceFields.map<IOption>((f) => ({
    label: f.extId,
    key: String(f.id),
    url: `/${customerSignature}/configuration/fields?item1=${f.id}`,
  }));
};
